import {
  parsePaymentSelectionKey,
  type PaymentProvider,
  type PaymentSelectionKey,
} from "@/services/student/checkout/checkout.interfaces";

export const CHECKOUT_PRIMARY_CTA_CLASS =
  "bg-success-subtle hover:bg-success-subtle/90 text-white focus-visible:ring-success-subtle";

export const CHECKOUT_CONTINUE_LABEL = "Continuar";
export const CHECKOUT_CONFIRM_PAYMENT_LABEL = "Confirmar pagamento";
export const CHECKOUT_PROCESSING_PAYMENT_LABEL = "Processando pagamento...";
export const CHECKOUT_COMPLETED_LABEL = "Pedido confirmado";
export const CHECKOUT_BACK_LABEL = "Voltar";

export const PAYMENT_AWAITING_HINT =
  "Ainda aguardando a confirmação. Se você já pagou, toque no botão abaixo para verificar novamente. Pode também deixar a página aberta — avisamos por e-mail assim que o acesso for liberado.";

export const PAYMENT_RETRY_BUTTON_LABEL = "Já paguei — verificar novamente";

export const PAYMENT_AWAITING_NAV_LABEL = "Aguardando confirmação...";

export function getPaymentPlaceholderHint(
  selectedPaymentKey: PaymentSelectionKey | null
): string {
  if (!selectedPaymentKey) {
    return "Escolha uma forma de pagamento e toque em Confirmar pagamento.";
  }

  const { method } = parsePaymentSelectionKey(selectedPaymentKey);
  if (method === "Pix") {
    return "Toque em Confirmar pagamento para ver o QR Code. O acesso libera quando o banco confirmar.";
  }
  if (method === "Card") {
    return "Toque em Confirmar pagamento. O formulário seguro aparece depois — você não digita PAN/CVV na OfCourse.";
  }
  if (method === "PayPal") {
    return "Toque em Confirmar pagamento. A confirmação acontece na janela do PayPal.";
  }

  return "Toque em Confirmar pagamento para iniciar.";
}

export const PAYMENT_CHANGE_METHOD_LABEL = "Trocar";

export const PAYMENT_PROVIDER_BADGE: Partial<Record<PaymentProvider, string>> = {
  MercadoPago: "Mercado Pago",
  Stripe: "Stripe",
  Paypal: "PayPal",
};

export function getPaymentMethodFallbackLabel(
  selectedPaymentKey: PaymentSelectionKey | null
): string {
  if (!selectedPaymentKey) return "Forma de pagamento";

  const { method } = parsePaymentSelectionKey(selectedPaymentKey);
  if (method === "Pix") return "PIX";
  if (method === "Card") return "Cartão de crédito";
  if (method === "PayPal") return "PayPal";

  return "Forma de pagamento";
}

export function getPaymentProviderBadgeLabel(
  selectedPaymentKey: PaymentSelectionKey | null
): string | null {
  if (!selectedPaymentKey) return null;
  const { provider } = parsePaymentSelectionKey(selectedPaymentKey);
  return PAYMENT_PROVIDER_BADGE[provider] ?? provider;
}

export function getPaymentInstrumentIntro(
  selectedPaymentKey: PaymentSelectionKey | null
): string {
  if (!selectedPaymentKey) return "Conclua o pagamento abaixo.";

  const { method } = parsePaymentSelectionKey(selectedPaymentKey);
  if (method === "Card") return "Preencha os dados no formulário seguro abaixo.";
  if (method === "Pix") return "Escaneie o QR ou copie o código.";
  if (method === "PayPal") return "Autorize o pagamento na janela do PayPal.";

  return "Conclua o pagamento abaixo.";
}

export function getPaymentFailedMessage(
  selectedPaymentKey: PaymentSelectionKey | null
): string {
  if (!selectedPaymentKey) {
    return "Pagamento não confirmado. Tente novamente ou escolha outra forma de pagamento.";
  }

  const { method } = parsePaymentSelectionKey(selectedPaymentKey);
  if (method === "Pix") {
    return "Pagamento não confirmado. Confira no app do banco se o PIX foi enviado ou escolha outra forma de pagamento.";
  }
  if (method === "Card") {
    return "Pagamento recusado. Confira os dados do cartão ou inicie um novo checkout.";
  }
  if (method === "PayPal") {
    return "Pagamento não confirmado no PayPal. Tente novamente ou escolha outra forma de pagamento.";
  }

  return "Pagamento não confirmado. Tente novamente ou escolha outra forma de pagamento.";
}

export const PAYMENT_PROCESSING_HEADLINE = "Processando pagamento";
export const PAYMENT_PROCESSING_SUBTEXT =
  "Aguarde enquanto confirmamos com o banco.";
export const PAYMENT_PROCESSING_SECURITY_HINT =
  "Seus dados estão protegidos pelo Mercado Pago.";
export const PAYMENT_SUCCESS_HEADLINE = "Compra confirmada!";
export const PAYMENT_SUCCESS_SUBTEXT = "Seu acesso já está liberado.";
